import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import API from "@/lib/api";
import { ENTITY_CONFIG } from "@/lib/entityConfig";

export default function AddEditDialog({ open, item, entityType, onClose, onSave }) {
  const config = ENTITY_CONFIG[entityType];
  const isEdit = !!item;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(item ? { ...item } : {});
      setError(null);
    }
  }, [item, open]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    const missing = config.fields.filter((f) => f.required && !form[f.key] && form[f.key] !== 0);
    if (missing.length) {
      setError(`Required: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await API.put(`/${config.apiPath}/${item.id}`, form);
      } else {
        await API.post(`/${config.apiPath}`, form);
      }
      onSave?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!config) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-card border-border" data-testid="add-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {isEdit ? "Edit" : "Add New"} {config.label.replace(/s$/, "")}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-2">
          <div className="grid grid-cols-2 gap-4 p-1">
            {config.fields.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                </Label>
                {field.type === "select" ? (
                  <Select
                    value={form[field.key] || ""}
                    onValueChange={(v) => handleChange(field.key, v)}
                  >
                    <SelectTrigger className="bg-background border-border" data-testid={`field-${field.key}`}>
                      <SelectValue placeholder={`Select ${field.label}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={field.type}
                    value={form[field.key] ?? ""}
                    onChange={(e) =>
                      handleChange(field.key, field.type === "number" ? (e.target.value === "" ? "" : parseFloat(e.target.value)) : e.target.value)
                    }
                    placeholder={field.placeholder || field.label}
                    className="bg-background border-border"
                    data-testid={`field-${field.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        {error && (
          <div className="text-sm text-red-500 bg-red-500/10 p-2 rounded-sm font-data" data-testid="form-error">{error}</div>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} data-testid="form-cancel">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="form-submit">
            {saving ? "Saving..." : isEdit ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
